import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Box,
  Typography,
  IconButton,
  Button,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Divider,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
  Autocomplete
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  LocationOn as LocationIcon,
  Assignment as AssignmentIcon,
  SwapHoriz as TransferIcon,
  Add as AddIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { jobApi, inventoryApi } from '../services/api.service';
import equipmentService from '../services/equipment.service';
import EquipmentSelector from '../components/EquipmentSelector';
import JobEquipmentPlanning from '../components/JobEquipmentPlanning';

/**
 * Site Detail Page
 * Shows detailed information about a specific site including equipment assignments
 */
export default function SiteDetailPage() {
  const { jobId, siteId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [job, setJob] = useState(null);
  const [site, setSite] = useState(null);
  const [siteEquipment, setSiteEquipment] = useState([]);
  
  // Notes editing
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesText, setNotesText] = useState('');

  // Dialogs
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [showStackDialog, setShowStackDialog] = useState(false);

  // Transfer state
  const [transferData, setTransferData] = useState({
    equipmentId: '',
    toSiteId: '',
    notes: ''
  });

  // Warehouse and rack data for display
  const [warehouses, setWarehouses] = useState([]);
  const [racks, setRacks] = useState([]);
  const [bays, setBays] = useState([]);

  // Stack location editing state
  const [stackForm, setStackForm] = useState({
    stackWarehouseId: null,
    stackRackId: null,
    stackBayId: null
  });

  // Load data
  useEffect(() => {
    if (jobId && siteId) {
      loadSiteData();
    }
  }, [jobId, siteId]);

  const loadSiteData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load job with sites
      const jobResponse = await jobApi.getJobById(jobId);
      setJob(jobResponse.data);
      
      // Find the specific site
      const currentSite = jobResponse.data.sites?.find(s => s.id.toString() === siteId.toString());
      if (!currentSite) {
        setError(`Site not found: ${siteId}`);
        return;
      }
      setSite(currentSite);
      setNotesText(currentSite.notes || '');

      // Load equipment for this site
      await loadSiteEquipment();
      
      // Load warehouses and racks for display
      await loadWarehouseData(jobResponse.data);
      
    } catch (err) {
      console.error('Error loading site data:', err);
      setError(err.message || 'Failed to load site data');
    } finally {
      setLoading(false);
    }
  };

  const loadWarehouseData = async (jobData) => {
    try {
      // Load ALL warehouses
      const warehouseResponse = await inventoryApi.getWarehouses();
      console.log('Warehouse response:', warehouseResponse);
      const warehouseData = warehouseResponse.data?.warehouses || warehouseResponse.data?.data || warehouseResponse.data || [];
      console.log('Warehouse data extracted:', warehouseData);
      setWarehouses(Array.isArray(warehouseData) ? warehouseData : []);

      // Load ALL racks (not filtered by warehouse)
      const rackResponse = await inventoryApi.getRacks();
      const rackData = rackResponse.data?.racks || rackResponse.data?.data || rackResponse.data || [];
      setRacks(Array.isArray(rackData) ? rackData : []);

      // Load ALL bays (not filtered by rack)
      const bayResponse = await inventoryApi.getBays();
      const bayData = bayResponse.data?.bays || bayResponse.data?.data || bayResponse.data || [];
      setBays(Array.isArray(bayData) ? bayData : []);
    } catch (err) {
      console.error('Error loading warehouse data:', err);
    }
  };

  const loadSiteEquipment = async () => {
    try {
      // Get all job equipment and filter for this site
      const response = await equipmentService.getJobEquipment(jobId);
      const equipment = response.equipment || response.data || response || [];
      const siteSpecificEquipment = Array.isArray(equipment) ? equipment.filter(item => 
        item.current_site_id && item.current_site_id.toString() === siteId.toString()
      ) : [];
      setSiteEquipment(siteSpecificEquipment);
    } catch (err) {
      console.error('Error loading site equipment:', err);
      // Don't set error for equipment loading failure, just log it
    }
  };

  // Calculate site stats
  const calculateSiteStats = () => {
    if (!siteEquipment || siteEquipment.length === 0) {
      return {
        totalItems: 0,
        receivedItems: 0,
        pendingItems: 0,
        receivedPercentage: 0
      };
    }

    let totalItems = 0;
    let receivedItems = 0;

    siteEquipment.forEach(item => {
      if (item) {
        const quantity = (item.quantity && !isNaN(item.quantity)) ? Number(item.quantity) : 1;
        totalItems += quantity;
        
        const status = item.status ? item.status.toLowerCase() : '';
        if (status === 'received' || status === 'delivered' || item.isReceived === true) {
          receivedItems += quantity;
        }
      }
    });

    const pendingItems = totalItems - receivedItems;
    const receivedPercentage = totalItems > 0 ? Math.round((receivedItems / totalItems) * 100) : 0;

    return {
      totalItems,
      receivedItems,
      pendingItems,
      receivedPercentage
    };
  };

  // Handle equipment assignment
  const handleAssignEquipment = async (selectedItems) => {
    if (!selectedItems || selectedItems.length === 0) {
      setShowAssignDialog(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      let successCount = 0;
      let errorCount = 0;
      
      for (const item of selectedItems) {
        try {
          await equipmentService.assignEquipment(jobId, {
            equipment_id: String(item.id),
            quantity: 1,
            site_id: String(siteId),
            status: 'assigned'
          });
          successCount++;
        } catch (itemError) {
          console.error(`Error assigning equipment ${item.id}:`, itemError);
          errorCount++;
        }
      }

      // Reload site equipment and job data to show updated assignments
      await Promise.all([
        loadSiteEquipment(),
        loadSiteData()
      ]);
      
      // Show success message
      if (successCount > 0) {
        const message = successCount === 1 
          ? `Successfully assigned 1 equipment item to ${site.name}` 
          : `Successfully assigned ${successCount} equipment items to ${site.name}`;
        console.log(message); // You can replace this with a proper success notification
        
        // Close dialog after successful assignment(s)
        setShowAssignDialog(false);
      }
      
      if (errorCount > 0) {
        setError(`Failed to assign ${errorCount} equipment item${errorCount > 1 ? 's' : ''}`);
      }
      
    } catch (err) {
      console.error('Error assigning equipment:', err);
      setError(err.message || 'Failed to assign equipment');
    } finally {
      setLoading(false);
    }
  };

  // Handle equipment transfer
  const handleTransferEquipment = async () => {
    if (!transferData.equipmentId || !transferData.toSiteId) {
      setError('Please select equipment and destination site');
      return;
    }

    try {
      setLoading(true);
      
      // Use the inventory API to transfer equipment between sites
      const transferPayload = {
        items: [{
          id: transferData.equipmentId,
          quantity: 1
        }],
        fromLocation: `Site: ${site.name}`,
        toLocation: transferData.toSiteId === '' ? 'Unassigned' : `Site: ${job.sites.find(s => s.id.toString() === transferData.toSiteId.toString())?.name || transferData.toSiteId}`,
        notes: transferData.notes || `Transferred from ${site.name}`,
        type: 'TRANSFER',
        jobId: jobId,
        fromSiteId: siteId,
        toSiteId: transferData.toSiteId || null
      };
      
      await inventoryApi.processTransfer(transferPayload);

      await loadSiteEquipment();
      setShowTransferDialog(false);
      setTransferData({ equipmentId: '', toSiteId: '', notes: '' });
      
    } catch (err) {
      console.error('Error transferring equipment:', err);
      setError(err.message || 'Failed to transfer equipment');
    } finally {
      setLoading(false);
    }
  };

  // Handle notes save
  const handleSaveNotes = async () => {
    try {
      setLoading(true);
      
      const updatedSite = { ...site, notes: notesText };
      await jobApi.updateJobSite(jobId, siteId, updatedSite);
      
      setSite(updatedSite);
      setEditingNotes(false);
      
    } catch (err) {
      console.error('Error saving notes:', err);
      setError(err.message || 'Failed to save notes');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/jobs/${jobId}`);
  };

  const siteStats = calculateSiteStats();

  // Get stack location display (prioritize site stack over job stack)
  const getStackLocationDisplay = () => {
    const warehouseId = site?.stackWarehouseId || job?.stackWarehouseId;
    const rackId = site?.stackRackId || job?.stackRackId;
    const bayId = site?.stackBayId || job?.stackBayId;

    if (!warehouseId) return 'Not assigned';

    // Safety checks for array types
    if (!Array.isArray(warehouses) || !Array.isArray(racks) || !Array.isArray(bays)) {
      return 'Not assigned';
    }

    const warehouse = warehouses.find(w => w.id === warehouseId);
    const rack = racks.find(r => r.id === rackId);
    const bay = bays.find(b => b.id === bayId);

    const parts = [warehouse?.name, rack?.name, bay?.name].filter(Boolean);
    return parts.length > 0 ? parts.join('-') : 'Not assigned';
  };

  // Handle opening stack dialog
  const handleOpenStackDialog = async () => {
    // Reload warehouse data to ensure we have fresh data
    if (job) {
      await loadWarehouseData(job);
    }

    setStackForm({
      stackWarehouseId: site?.stackWarehouseId || null,
      stackRackId: site?.stackRackId || null,
      stackBayId: site?.stackBayId || null
    });
    setShowStackDialog(true);
  };

  // Handle saving stack location
  const handleSaveStack = async () => {
    try {
      setLoading(true);
      setError(null);

      const updatedSite = {
        ...site,
        stackWarehouseId: stackForm.stackWarehouseId,
        stackRackId: stackForm.stackRackId,
        stackBayId: stackForm.stackBayId
      };

      await jobApi.updateJobSite(jobId, siteId, updatedSite);

      setSite(updatedSite);
      setShowStackDialog(false);

      // Reload site data to ensure consistency
      await loadSiteData();

    } catch (err) {
      console.error('Error saving stack location:', err);
      setError(err.message || 'Failed to save stack location');
    } finally {
      setLoading(false);
    }
  };

  // Handle warehouse change in stack dialog
  const handleWarehouseChange = (newValue) => {
    setStackForm({
      ...stackForm,
      stackWarehouseId: newValue?.id || null
    });
  };

  // Handle rack change in stack dialog
  const handleRackChange = (newValue) => {
    setStackForm({
      ...stackForm,
      stackRackId: newValue?.id || null
    });
  };

  // Loading state
  if (loading && !site) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error || !site) {
    return (
      <Box sx={{ mt: 3 }}>
        <Alert severity="error">
          {error || 'Site not found'}
        </Alert>
        <Button 
          startIcon={<BackIcon />} 
          onClick={handleBack} 
          sx={{ mt: 2 }}
        >
          Back to Job Details
        </Button>
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={handleBack} sx={{ mr: 1 }}>
          <BackIcon />
        </IconButton>
        <Box>
          <Typography variant={isMobile ? "h5" : "h4"} component="h1">
            {site.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            Job: {job?.jobNumber || job?.id} - {job?.name}
          </Typography>
        </Box>
      </Box>

      {/* Site Details Card - Matching JobDetailPage Design */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            {/* Compact Site Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
              <Box sx={{ flex: 1 }}>
                {/* Title Row */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {site.name} - {job.client}
                  </Typography>
                  <Chip 
                    label={job.status || 'Planning'} 
                    variant="outlined"
                    size="small"
                    sx={{ 
                      fontWeight: 500,
                      borderColor: 'text.secondary',
                      color: 'text.secondary'
                    }}
                  />
                </Box>

                {/* Details Row */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
                  {site.address && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                      Address: {site.address}
                    </Typography>
                  )}
                  {site.contactPerson && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                      Contact: {site.contactPerson} {site.phone && `• ${site.phone}`}
                    </Typography>
                  )}
                  {job.startDate && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                      Dates: {new Date(job.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      {job.endDate && (
                        <> - {new Date(job.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</>
                      )}
                    </Typography>
                  )}
                </Box>

                {/* Progress and Stack Row */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                    Equipment Progress: <strong>{siteStats.receivedItems} received / {siteStats.totalItems} planned</strong>
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                      Stack: {getStackLocationDisplay()}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleOpenStackDialog}
                      sx={{ minWidth: 'auto', py: 0.25, px: 1, fontSize: '0.75rem' }}
                    >
                      Edit Stacks
                    </Button>
                  </Box>
                </Box>
              </Box>

              {/* Notes Section - Right Side */}
              <Box sx={{ 
                minWidth: { xs: 0, md: 400 }, 
                ml: { xs: 0, md: 4 },
                mt: { xs: 3, md: 0 },
                maxWidth: '100%',
                flexShrink: 1
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                    Site Notes
                  </Typography>
                  {!editingNotes && (
                    <IconButton 
                      size="small" 
                      onClick={() => setEditingNotes(true)}
                      sx={{ color: 'text.secondary' }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                
                {editingNotes ? (
                  <Box>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      value={notesText}
                      onChange={(e) => setNotesText(e.target.value)}
                      placeholder="Enter site notes..."
                      variant="outlined"
                      size="small"
                      sx={{ mb: 1 }}
                    />
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <Button 
                        size="small" 
                        onClick={() => {
                          setNotesText(site.notes || '');
                          setEditingNotes(false);
                        }}
                      >
                        Cancel
                      </Button>
                      <Button 
                        size="small" 
                        variant="contained" 
                        onClick={handleSaveNotes}
                        disabled={notesText === (site.notes || '')}
                      >
                        Save
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Typography 
                    variant="body2" 
                    color={site.notes ? "text.primary" : "text.secondary"}
                    onClick={() => setEditingNotes(true)}
                    sx={{ 
                      fontStyle: site.notes ? 'normal' : 'italic',
                      whiteSpace: 'pre-wrap',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      fontSize: '0.9rem',
                      p: 2,
                      bgcolor: 'grey.50',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'grey.200',
                      minHeight: '4rem',
                      display: 'flex',
                      alignItems: 'flex-start',
                      cursor: 'pointer',
                      width: '100%',
                      maxWidth: '100%',
                      boxSizing: 'border-box',
                      '&:hover': {
                        bgcolor: 'grey.100',
                        borderColor: 'grey.300'
                      }
                    }}
                  >
                    {site.notes || 'No notes. Click to add.'}
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Site Equipment Planning - Pass siteId for site-specific view */}
      <JobEquipmentPlanning jobId={jobId} siteId={siteId} />

      {/* Assignment Dialog */}
      <Dialog 
        open={showAssignDialog} 
        onClose={() => setShowAssignDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Assign Equipment to {site.name}</DialogTitle>
        <DialogContent>
          <EquipmentSelector
            multiple={true}
            mode="available"
            onSelect={handleAssignEquipment}
            showAvailability={true}
            hideActions={false}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAssignDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Transfer Dialog */}
      <Dialog
        open={showTransferDialog}
        onClose={() => setShowTransferDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Transfer Equipment</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Equipment to Transfer</InputLabel>
                  <Select
                    value={transferData.equipmentId}
                    onChange={(e) => setTransferData(prev => ({ ...prev, equipmentId: e.target.value }))}
                    label="Equipment to Transfer"
                  >
                    {siteEquipment.map(item => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.description || `Equipment Item ${item.id}`} (ID: {item.id})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>To Site</InputLabel>
                  <Select
                    value={transferData.toSiteId}
                    onChange={(e) => setTransferData(prev => ({ ...prev, toSiteId: e.target.value }))}
                    label="To Site"
                  >
                    <MenuItem value="">Unassigned</MenuItem>
                    {job?.sites?.filter(s => s.id.toString() !== siteId.toString()).map(s => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Transfer Notes"
                  value={transferData.notes}
                  onChange={(e) => setTransferData(prev => ({ ...prev, notes: e.target.value }))}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTransferDialog(false)}>Cancel</Button>
          <Button
            onClick={handleTransferEquipment}
            variant="contained"
            color="primary"
            startIcon={<TransferIcon />}
          >
            Transfer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Stack Location Dialog */}
      <Dialog
        open={showStackDialog}
        onClose={() => setShowStackDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Site Stack Location</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Autocomplete
                  options={Array.isArray(warehouses) ? warehouses : []}
                  getOptionLabel={(option) => option.name || ''}
                  value={Array.isArray(warehouses) ? warehouses.find(w => w.id === stackForm.stackWarehouseId) || null : null}
                  onChange={(event, newValue) => handleWarehouseChange(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Stack Warehouse"
                      helperText="Where this site's equipment is staged"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  options={Array.isArray(racks) ? racks : []}
                  getOptionLabel={(option) => option.name || ''}
                  value={Array.isArray(racks) ? racks.find(r => r.id === stackForm.stackRackId) || null : null}
                  onChange={(event, newValue) => handleRackChange(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Stack Rack"
                      helperText="Select a rack for this site's equipment"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  options={Array.isArray(bays) ? bays : []}
                  getOptionLabel={(option) => option.name || ''}
                  value={Array.isArray(bays) ? bays.find(b => b.id === stackForm.stackBayId) || null : null}
                  onChange={(event, newValue) => {
                    setStackForm(prev => ({
                      ...prev,
                      stackBayId: newValue?.id || null
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Stack Bay"
                      helperText="Select a bay for this site's equipment"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowStackDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSaveStack}
            variant="contained"
            color="primary"
            disabled={!stackForm.stackWarehouseId}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </Container>
  );
}